import { useState } from "react"
import { Link } from "react-router-dom"
import { Shield, Upload, FileAudio, AlertCircle, Download, Mail, ArrowLeft } from "lucide-react"

const InsuranceAnalyzer = () => {
  const [file, setFile] = useState(null)
  const [callType, setCallType] = useState("quote")
  const [insuranceType, setInsuranceType] = useState("auto")
  const [agent, setAgent] = useState("")
  const [loading, setLoading] = useState(false)
  const [transcript, setTranscript] = useState("")
  const [analysis, setAnalysis] = useState("")
  const [error, setError] = useState("")

  const callTypes = [
    { value: "quote", label: "Quote Call", description: "Initial quote request" },
    { value: "policy_review", label: "Policy Review", description: "Existing policy evaluation" },
    { value: "claims", label: "Claims Support", description: "Claims assistance call" },
    { value: "renewal", label: "Renewal", description: "Policy renewal discussion" },
  ]

  const insuranceTypes = [
    { value: "medical", label: "Medical/Health", icon: "🏥" },
    { value: "personal", label: "Personal/Life", icon: "👤" },
    { value: "auto", label: "Auto/Vehicle", icon: "🚗" },
    { value: "home", label: "Home/Property", icon: "🏠" },
  ]

  const handleUpload = async () => {
    if (!file) {
      setError("Please select a file")
      return
    }

    const formData = new FormData()
    formData.append("file", file)
    formData.append("type", callType)
    formData.append("agent", agent)
    // Add industry and sub-type for backend
    formData.append("industry", "insurance")
    formData.append("subtype", insuranceType)

    setLoading(true)
    setError("")

    try {
      const token = localStorage.getItem("token")
      const response = await fetch("https://call-analyzer-backend.onrender.com/analyze", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      })

      if (!response.ok) {
        const data = await response.json()
        throw new Error(data.detail || "Analysis failed")
      }

      const data = await response.json()
      setTranscript(data.transcript)
      setAnalysis(data.analysis)
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-6 px-6 shadow-lg">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center">
            <Shield className="h-8 w-8 mr-3" />
            <div>
              <h1 className="text-2xl font-bold">Insurance Call Analyzer</h1>
              <p className="text-blue-100">Analyze insurance sales calls across all coverage types</p>
            </div>
          </div>
          <Link to="/analyzer" className="flex items-center text-white hover:text-blue-100 transition">
            <ArrowLeft className="h-5 w-5 mr-1" />
            All Analyzers
          </Link>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Upload Section */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-6">Upload Insurance Call</h2>

              {/* Insurance Type Selection */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Insurance Type
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {insuranceTypes.map((type) => (
                    <button
                      key={type.value}
                      onClick={() => setInsuranceType(type.value)}
                      className={`p-3 rounded-lg border-2 transition ${
                        insuranceType === type.value
                          ? "border-blue-500 bg-blue-50"
                          : "border-gray-200 hover:border-blue-300"
                      }`}
                    >
                      <span className="text-2xl">{type.icon}</span>
                      <p className="text-xs mt-1 font-medium">{type.label}</p>
                    </button>
                  ))}
                </div>
              </div>

              {/* Call Type Selection */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Call Type
                </label>
                <select
                  value={callType}
                  onChange={(e) => setCallType(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  {callTypes.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  {callTypes.find(t => t.value === callType)?.description}
                </p>
              </div>

              {/* Agent Name */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Agent Name
                </label>
                <input
                  type="text"
                  value={agent}
                  onChange={(e) => setAgent(e.target.value)}
                  placeholder="Enter agent name"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              {/* File Upload */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Audio File
                </label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-blue-500 transition">
                  <FileAudio className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                  <input
                    type="file"
                    accept=".mp3,.wav,.m4a,.mp4,.mpeg,.mpga,.webm,.ogg"
                    onChange={(e) => setFile(e.target.files[0])}
                    className="hidden"
                    id="file-upload"
                  />
                  <label
                    htmlFor="file-upload"
                    className="cursor-pointer text-blue-600 hover:text-blue-700 font-medium"
                  >
                    Choose file
                  </label>
                  {file && (
                    <p className="text-sm text-gray-600 mt-2">{file.name}</p>
                  )}
                </div>
              </div>

              {/* Error Message */}
              {error && (
                <div className="mb-6 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start">
                  <AlertCircle className="h-5 w-5 text-red-600 mr-2 flex-shrink-0" />
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              )}

              {/* Upload Button */}
              <button
                onClick={handleUpload}
                disabled={loading || !file}
                className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-3 rounded-lg font-semibold hover:shadow-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <span className="flex items-center justify-center">
                    <Upload className="animate-spin h-5 w-5 mr-2" />
                    Analyzing Insurance Call...
                  </span>
                ) : (
                  <span className="flex items-center justify-center">
                    <Upload className="h-5 w-5 mr-2" />
                    Analyze Call
                  </span>
                )}
              </button>

              {/* Insurance-Specific Tips */}
              <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                <h3 className="font-semibold text-blue-900 mb-2">Insurance Call Tips</h3>
                <ul className="text-sm text-blue-700 space-y-1">
                  <li>• Build trust with empathy</li>
                  <li>• Simplify complex coverage terms</li>
                  <li>• Focus on protection, not price</li>
                  <li>• Address pre-existing conditions</li>
                  <li>• Explain deductibles clearly</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Results Section */}
          <div className="lg:col-span-2">
            {analysis || transcript ? (
              <div className="space-y-6">
                {/* Analysis */}
                {analysis && (
                  <div className="bg-white rounded-xl shadow-lg p-6">
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <h2 className="text-xl font-bold text-gray-900">Insurance Call Analysis</h2>
                        <p className="text-sm text-gray-600">
                          {insuranceTypes.find(t => t.value === insuranceType)?.label} • {callTypes.find(t => t.value === callType)?.label}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <button className="p-2 text-gray-600 hover:text-blue-600 transition">
                          <Download className="h-5 w-5" />
                        </button>
                        <button className="p-2 text-gray-600 hover:text-blue-600 transition">
                          <Mail className="h-5 w-5" />
                        </button>
                      </div>
                    </div>
                    <div className="prose max-w-none">
                      <pre className="whitespace-pre-wrap text-sm text-gray-700 font-sans">
                        {analysis}
                      </pre>
                    </div>
                  </div>
                )}

                {/* Transcript */}
                {transcript && (
                  <div className="bg-white rounded-xl shadow-lg p-6">
                    <h2 className="text-xl font-bold text-gray-900 mb-4">Call Transcript</h2>
                    <div className="prose max-w-none">
                      <pre className="whitespace-pre-wrap text-sm text-gray-700 font-sans">
                        {transcript}
                      </pre>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="bg-white rounded-xl shadow-lg p-12 text-center">
                <Shield className="h-16 w-16 text-blue-200 mx-auto mb-4" />
                <h2 className="text-xl font-semibold text-gray-900 mb-2">
                  Ready to Analyze Insurance Calls
                </h2>
                <p className="text-gray-600">
                  Upload an insurance sales call to receive AI-powered analysis tailored for your coverage type
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default InsuranceAnalyzer
